import { db } from '@util/firebase';
import {
  collection,
  CollectionReference,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  commentDocumentSchema,
  type CommentDocument,
  type PostCommentInput,
} from 'models/comment';

type CommentAuthor = { uid: string; username?: string; photo?: string };

const getProductCommentsRef = (product_id: string) =>
  collection(
    db,
    'products',
    product_id,
    'comments'
  ) as CollectionReference<CommentDocument>;

export const getProductComments = async (
  product_id: string
): Promise<CommentDocument[]> => {
  const productCommentsRef = getProductCommentsRef(product_id);

  const q = query(productCommentsRef, orderBy('created', 'asc'));

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ ...d.data(), id: d.id }))
    .filter((comment) => !comment.deleted);
};

export const postProductComment = async (
  input: PostCommentInput,
  author: CommentAuthor
): Promise<CommentDocument> => {
  const commentRef = doc(getProductCommentsRef(input.product_id));
  const comment = commentDocumentSchema.parse({
    id: commentRef.id,
    product_id: input.product_id,
    replying_to: input.replying_to ?? null,
    uid: author.uid,
    comment: input.comment,
    created: Date.now(),
    username: author.username ?? '',
    profile_picture: author.photo ?? '',
    deleted: false,
  });
  await setDoc(commentRef, comment);
  return comment;
};

// soft-delete handled client-side; only the comment author may flip `deleted`
// to true (enforced by firestore.rules on products/{id}/comments/{id})
export const deleteProductComment = async (
  product_id: string,
  comment_id: string
): Promise<void> => {
  const commentRef = doc(db, 'products', product_id, 'comments', comment_id);
  await updateDoc(commentRef, { deleted: true });
};
